import { firstValueFrom } from 'rxjs'
import connectToggleRefinement, {
    ToggleRefinementConnectorParams as CParams,
    ToggleRefinementWidgetDescription as WDescription,
} from 'instantsearch.js/es/connectors/toggle-refinement/connectToggleRefinement'
import { Renderer } from 'instantsearch.js/es/types'

import { Component, forwardRef, Inject, Input, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'

import { AlgoliaInstantsearchComponent } from '@app-domains/algolia/components/algolia-instantsearch.component'
import { AbstractInstantsearchWidgetComponent } from '@app-domains/algolia/abstract-instantsearch-widget.component'

type State = WDescription['renderState']

@Component({
    selector: 'app-toggle-refinement',
    template: `
        <div class="toggle-refinement"
             [class.filter-has-no-matching-items]="! state?.canRefine"
        >
            <span class="toggle-refinement__text">{{ localisedAttribute }}</span>
            <input
                type="checkbox"
                [id]="attribute"
                [checked]="isChecked"
                (change)="toggle()"
                class="toggle-refinement__checkbox"
            />
            <label class="toggle-refinement__label" [for]="attribute">
                <span class="toggle-refinement__inner"></span>
                <span class="toggle-refinement__switch"></span>
            </label>
        </div>
    `,
    styleUrls: ['./toggle.component.scss'],
})
export class ToggleComponent extends AbstractInstantsearchWidgetComponent<
WDescription,
CParams
> implements OnInit {

    public override readonly widgetName = 'ToggleRefinement'

    @Input()
    public attribute: string

    @Input()
    public localisedAttribute: string

    public isChecked: boolean = false

    constructor(
        @Inject(forwardRef(() => AlgoliaInstantsearchComponent))
        public instantSearchInstance: AlgoliaInstantsearchComponent,
        private route: ActivatedRoute,
        private router: Router,
    ) {
        super()
    }

    public override ngOnInit(): void {
        this.createWidget(connectToggleRefinement, {
            attribute: this.attribute,
        })

        super.ngOnInit()

        void this.initialiseRefinementFromUrl()
    }

    public toggle(): void {
        this.isChecked = ! this.isChecked
        this.state?.refine({ isRefined: ! this.isChecked })

        this.setUrl()
    }

    private setUrl(): void {
        void this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { [this.attribute]: this.isChecked || null },
            queryParamsHandling: 'merge',
        })
    }

    private async initialiseRefinementFromUrl(): Promise<void> {
        const queryParams = await firstValueFrom(this.route.queryParamMap)
        const isRefined = queryParams.get(this.attribute) === 'true'

        if (isRefined !== this.isChecked) {
            this.isChecked = isRefined

            this.state?.refine({ isRefined: ! this.isChecked })
        }
    }

    public override updateState: Renderer<State, CParams> = async (state, isFirstRendering): Promise<void> => {
        this.state = state

        if (isFirstRendering) {
            return
        }

        this.isChecked = this.state.value.isRefined
    }

}
